import {createSelector} from 'reselect';
import {emptyArray} from '@packages/utilities/fp/utils';
import {DesignerStores} from '@packages/systems/designer/flux';
import {BreakpointID, CssPropertyMap} from '@packages/systems/style/types';
import {cacheMemo} from '@packages/utilities/memo';
import {getStyleProperties} from '@packages/systems/style/utils';
import {
  ERecord,
  EList,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {qualify, pluginName, typeName} from '@packages/systems/wfdl-Qualified';
import {
  useCssPropertyMap,
  useCssVariables,
  useCssVariableCollections,
  useSlugMap,
} from '@packages/systems/render';
import {
  ColorPrimitive,
  CustomRawPrimitive,
  convertStringToVariableId,
  getVariableIdFromPlaceholder,
  renderAnyCssValue,
  VariableReference,
} from '@packages/systems/core/utils/StyleVariables';
import {extractCssCustomValue} from '@packages/systems/style/raw-values';
import {
  isSwatchPlaceholder,
  getSwatchIdFromPlaceholder,
} from '@packages/systems/style-legacy/swatches';

function selectCssPropertyMap(state: DesignerStores) {
  return state.StyleBlockStore.cssPropertyMap;
}

/**
 * A mock body element with no classes applied.
 *
 * We pass this to `getStyleProperties`, which in turn should in effect look up
 * our default body tag styles.
 */
const DUMMY_BODY_ELEMENT: ExpressionElement = {
  id: 'dummy-body',
  type: qualify(pluginName('Body'), typeName('Body')),
  data: ERecord({
    children: EList([]),
  }),
};

export const getBodyBackgroundColorValue =
  (breakpointId: BreakpointID) =>
  (
    cssPropertyMap: CssPropertyMap
  ): ColorPrimitive | VariableReference | CustomRawPrimitive => {
    const styleProperties =
      getStyleProperties(breakpointId)(null)(null)(cssPropertyMap)(
        DUMMY_BODY_ELEMENT
      );

    const colorString = styleProperties.get('background-color') ?? 'white';

    // convert legacy global swatches to variable references
    if (isSwatchPlaceholder(colorString)) {
      const swatchId = getSwatchIdFromPlaceholder(colorString) ?? '';
      return {
        type: 'ref',
        value: {
          variableId: convertStringToVariableId(`variable-${swatchId}`),
        },
      };
    }

    const possiblyCustomValue = extractCssCustomValue(colorString);

    let colorValue:
      | ColorPrimitive
      | VariableReference
      | CustomRawPrimitive
      | null = null;
    if (possiblyCustomValue !== colorString) {
      colorValue = {
        type: 'raw',
        value: possiblyCustomValue,
      };
    } else {
      const possibleVariableId = getVariableIdFromPlaceholder(colorString);
      if (possibleVariableId) {
        colorValue = {
          type: 'ref',
          value: {
            variableId: possibleVariableId,
          },
        };
      }
    }

    if (colorValue == null) {
      return {
        type: 'color',
        value: colorString,
      };
    }

    return colorValue;
  };

export const selectBodyBackgroundColorValue = cacheMemo(20)(
  (breakpointId: BreakpointID) =>
    createSelector(
      selectCssPropertyMap,
      getBodyBackgroundColorValue(breakpointId)
    )
);

export const useBodyBackgroundColor = (breakpointId: BreakpointID) => {
  const cssPropertyMap = useCssPropertyMap();
  const colorValue = getBodyBackgroundColorValue(breakpointId)(cssPropertyMap);

  const renderedColor: string | Error = renderAnyCssValue(
    colorValue,
    useCssVariables(),
    useCssVariableCollections(),
    useSlugMap(),
    emptyArray
  );

  if (renderedColor instanceof Error) {
    return 'white';
  }

  return renderedColor;
};
